"use client";

import type { ShowAttendanceStatus } from "@workspace/types";
import {
	RiArrowDownSLine,
	RiCalendarCheckFill,
	RiCalendarCheckLine,
	RiCheckLine,
	RiCloseLine,
	RiSignpostFill,
	RiSignpostLine,
} from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import { Spinner } from "@workspace/ui/components/spinner";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";
import { useRealtimeShowAttendance } from "@/hooks/features/show/use-realtime-show-attendance";
import { useCurrentUserDisplay } from "@/hooks/shared/use-auth";

interface ShowAttendanceButtonProps {
	showId: string;
	showIsPast: boolean;
	className?: string;
	size?: "sm" | "default" | "lg";
	onStatusChange?: (newStatus: ShowAttendanceStatus | null) => void;
}

/**
 * Show Attendance Button with Real-time Updates
 *
 * This button uses Supabase Realtime for instant cross-tab/device synchronization.
 * No cache invalidation needed - database events drive all updates.
 */
export function ShowAttendanceButton({
	showId,
	showIsPast,
	className,
	size = "default",
	onStatusChange,
}: ShowAttendanceButtonProps) {
	// Get current user for real-time subscription
	const { data: currentUser } = useCurrentUserDisplay();

	// Real-time show attendance - single source of truth
	const { status, updateAttendance, isPending } = useRealtimeShowAttendance({
		showId,
		userId: currentUser?.id || null,
		enabled: !!currentUser?.id && !!showId,
	});

	const handleStatusChange = async (newStatus: ShowAttendanceStatus | null) => {
		// Skip if selecting the same status (except when removing)
		if (newStatus === status && newStatus !== null) return;

		// Update via real-time hook
		await updateAttendance(newStatus);

		// Notify parent component if needed
		onStatusChange?.(newStatus);
	};

	// Get button configuration based on status
	const getButtonConfig = () => {
		if (!status) {
			return {
				label: showIsPast ? "Wanted to Go" : "Want to Go",
				icon: RiSignpostLine,
				variant: "secondary" as const,
			};
		}

		switch (status) {
			case "want_to_go":
				return {
					label: showIsPast ? "Wanted to Go" : "Want to Go",
					icon: RiSignpostFill,
					variant: "default" as const,
				};
			case "going":
				return {
					label: showIsPast ? "Went" : "Going",
					icon: RiCalendarCheckFill,
					variant: "default" as const,
				};
			default:
				return {
					label: showIsPast ? "Wanted to Go" : "Want to Go",
					icon: RiSignpostLine,
					variant: "secondary" as const,
				};
		}
	};

	const config = getButtonConfig();
	const Icon = config.icon;

	// Don't render if user is not logged in
	if (!currentUser?.id) {
		return null;
	}

	return (
		<div className={cn("flex", className)}>
			{/* Main button */}
			<Button
				variant={config.variant}
				size={size}
				onClick={(e) => {
					e.stopPropagation();
					e.preventDefault();
					if (!status) {
						handleStatusChange("want_to_go");
					}
				}}
				disabled={isPending}
				className={cn(
					"rounded-r-none",
					status === "going" &&
						"bg-radix-green-9 hover:bg-radix-green-10 text-radix-green-1",
				)}
			>
				{isPending ? (
					<Spinner className="h-4 w-4 animate-spin" />
				) : (
					<Icon className="h-4 w-4" />
				)}
				{config.label}
			</Button>

			{/* Dropdown */}
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant={config.variant}
						size={size}
					onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
				}}
						className={cn(
							"rounded-l-none border-l-0 px-1",
							status === "going" &&
								"bg-radix-green-9 hover:bg-radix-green-10 text-radix-green-1",
						)}
						disabled={isPending}
					>
						<RiArrowDownSLine className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-48">
					{/* Want to Go / Wanted to Go */}
					<DropdownMenuItem
						onClick={(e) => {
							e.stopPropagation();
					e.preventDefault();
							handleStatusChange("want_to_go");
						}}
					>
						<RiSignpostLine className="h-4 w-4" />
						{showIsPast ? "Wanted to Go" : "Want to Go"}
						{status === "want_to_go" && (
							<RiCheckLine className="ml-auto h-4 w-4" />
						)}
					</DropdownMenuItem>

					{/* Going / Went */}
					<DropdownMenuItem
						onClick={(e) => {
							e.stopPropagation();
					e.preventDefault();
							handleStatusChange("going");
						}}
					>
						<RiCalendarCheckLine className="h-4 w-4" />
						{showIsPast ? "Went" : "Going"}
						{status === "going" && (
							<RiCheckLine className="ml-auto h-4 w-4" />
						)}
					</DropdownMenuItem>

					{/* Remove */}
					{status && (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								onClick={(e) => { e.stopPropagation(); handleStatusChange(null); }}
								className="text-destructive"
							>
								<RiCloseLine className="h-4 w-4" />
								Remove Status
							</DropdownMenuItem>
						</>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}